import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Search, MapPin, Building2, Lock, Send, Filter, ExternalLink } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const SECTEURS = [
  { value: "all", label: "Tous secteurs" },
  { value: "industrie", label: "Industrie" },
  { value: "services", label: "Services" },
  { value: "commerce", label: "Commerce" },
  { value: "artisanat", label: "Artisanat" },
  { value: "tech", label: "Tech" },
  { value: "sante", label: "Santé" },
  { value: "batiment", label: "BTP" },
  { value: "restauration", label: "Restauration" },
];

const LOCALISATIONS = [
  { value: "all", label: "Toutes régions" },
  { value: "aura", label: "Auvergne-Rhône-Alpes" },
  { value: "paca", label: "PACA" },
  { value: "occitanie", label: "Occitanie" },
];

const BUDGETS = [
  { value: "all", label: "Tous budgets" },
  { value: "100000", label: "Max 100k€" },
  { value: "500000", label: "Max 500k€" },
  { value: "1000000", label: "Max 1M€" },
  { value: "5000000", label: "Max 5M€" },
];

// Demo opportunities for display with realistic data
const DEMO_OPPORTUNITES = [];

export default function Opportunites() {
  const navigate = useNavigate();
  const [repreneur, setRepreneur] = useState(null);
  const [opportunites, setOpportunites] = useState([]);
  const [filteredOpportunites, setFilteredOpportunites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isQualified, setIsQualified] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Filters
  const [secteurFilter, setSecteurFilter] = useState("all");
  const [localisationFilter, setLocalisationFilter] = useState("all");
  const [budgetFilter, setBudgetFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [selectedOpportunite, setSelectedOpportunite] = useState(null);
  const [viewingDetails, setViewingDetails] = useState(null);
  const [interestMessage, setInterestMessage] = useState("");
  const [sendingInterest, setSendingInterest] = useState(false);
  const [interestedIds, setInterestedIds] = useState(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Check if repreneur is logged in via Token
      const token = localStorage.getItem("access_token");
      const userType = localStorage.getItem("user_type");

      const params = new URLSearchParams();
      if (secteurFilter && secteurFilter !== "all") params.append("secteur", secteurFilter);
      if (localisationFilter && localisationFilter !== "all") params.append("localisation", localisationFilter);
      if (budgetFilter && budgetFilter !== "all") params.append("budget_max", budgetFilter);

      const getFilteredDemoData = () => {
        let filteredDemo = [...DEMO_OPPORTUNITES];
        if (secteurFilter && secteurFilter !== "all") {
          filteredDemo = filteredDemo.filter((o) => o.secteur === secteurFilter);
        }
        if (localisationFilter && localisationFilter !== "all") {
          filteredDemo = filteredDemo.filter((o) => o.localisation === localisationFilter);
        }
        if (budgetFilter && budgetFilter !== "all") {
          filteredDemo = filteredDemo.filter(
            (o) => o.valorisation_min <= parseInt(budgetFilter, 10),
          );
        }
        return filteredDemo;
      };

      if (token && userType === "repreneur") {
        setIsLoggedIn(true);
        try {
          const profileRes = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const rep = profileRes.data;
          setRepreneur(rep);
          setIsQualified(rep.validation === true);

          // Fetch user interests
          try {
            const interestsRes = await axios.get(`${API}/repreneurs/me/interets-opportunites`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const ids = new Set(interestsRes.data.map(opp => opp.id));
            setInterestedIds(ids);
          } catch (interestErr) {
            console.error("Error fetching interests:", interestErr);
          }
        } catch (err) {
          console.error("Error fetching profile:", err);
          if (err.response?.status === 401) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("user_type");
            setIsLoggedIn(false);
            navigate("/login");
          }
        }
      } else {
        // Fallback to legacy check or guest
        try {
          const storedRepreneur = localStorage.getItem("repreneur_result");
          if (storedRepreneur) {
            const rep = JSON.parse(storedRepreneur);
            setRepreneur(rep);
            setIsQualified(rep.validation === true);
          }
        } catch (e) {
          console.log("No repreneur data found");
        }
      }

      // Load opportunities (use demo data + any from API)
      try {
        const response = await axios.get(`${API}/opportunites?${params.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        setOpportunites([...getFilteredDemoData(), ...response.data]);
      } catch {
        // If API fails, just use filtered demo data
        setOpportunites(getFilteredDemoData());
      }
    } catch (error) {
      console.error(error);
      setOpportunites(DEMO_OPPORTUNITES);
    } finally {
      setLoading(false);
    }
  }, [budgetFilter, localisationFilter, navigate, secteurFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filterOpportunites = useCallback(() => {
    let filtered = [...opportunites];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.titre.toLowerCase().includes(query) ||
          o.description.toLowerCase().includes(query),
      );
    }

    setFilteredOpportunites(filtered);
  }, [opportunites, searchQuery]);

  useEffect(() => {
    filterOpportunites();
  }, [filterOpportunites]);

  const handleShowInterest = async () => {
    if (!repreneur || !selectedOpportunite) {
      toast.error("Vous devez être connecté pour manifester votre intérêt.");
      return;
    }

    if (interestedIds.has(selectedOpportunite.id)) {
      toast.error("Vous avez déjà manifesté votre intérêt pour cette opportunité.");
      return;
    }
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      toast.error("Veuillez vous reconnecter pour manifester votre intérêt.");
      navigate("/login");
      return;
    }
    
    setSendingInterest(true);
    try {
      await axios.post(`${API}/interets`, {
        repreneur_id: repreneur.id,
        opportunite_id: selectedOpportunite.id,
        message: interestMessage,
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000 // 10 second timeout
      });
      toast.success("Votre intérêt a été enregistré ! Éric vous contactera rapidement.");
      setInterestedIds(prev => new Set([...prev, selectedOpportunite.id]));
      setSelectedOpportunite(null);
      setInterestMessage("");
    } catch (error) {
      console.error("Error sending interest:", error);
      
      // Handle specific error cases
      if (error.code === "ERR_NETWORK" || error.message.includes("Network Error")) {
        // Network error - store in localStorage to retry later
        const pendingInterests = JSON.parse(localStorage.getItem("pending_interests") || "[]");
        pendingInterests.push({
          repreneur_id: repreneur.id,
          opportunite_id: selectedOpportunite.id,
          message: interestMessage,
          timestamp: new Date().toISOString(),
          opportunite_titre: selectedOpportunite.titre
        });
        localStorage.setItem("pending_interests", JSON.stringify(pendingInterests));
        
        toast.warning("Problème de connexion au serveur. Votre demande a été sauvegardée et sera envoyée automatiquement lorsque la connexion sera rétablie.", {
          duration: 5000,
        });
        setSelectedOpportunite(null);
        setInterestMessage("");
      } else if (error.response?.status === 401) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user_type");
        setIsLoggedIn(false);
        navigate("/login");
        toast.error("Votre session a expiré. Veuillez vous reconnecter.");
      } else if (error.response?.status === 403) {
        toast.error("Votre profil doit être qualifié pour manifester votre intérêt.");
      } else if (error.response?.status === 500) {
        toast.error("Erreur serveur. Notre équipe a été notifiée. Veuillez réessayer plus tard.");
      } else {
        toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      }
    } finally {
      setSendingInterest(false);
    }
  };

  // Retry pending interests when component mounts
  useEffect(() => {
    const retryPendingInterests = async () => {
      const pendingInterests = JSON.parse(localStorage.getItem("pending_interests") || "[]");
      if (pendingInterests.length === 0) return;
      
      const token = localStorage.getItem("access_token");
      if (!token) return;
      
      console.log(`Retrying ${pendingInterests.length} pending interests...`);
      
      for (let i = 0; i < pendingInterests.length; i++) {
        const interest = pendingInterests[i];
        try {
          await axios.post(`${API}/interets`, {
            repreneur_id: interest.repreneur_id,
            opportunite_id: interest.opportunite_id,
            message: interest.message,
          }, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            timeout: 10000
          });
          console.log(`Successfully sent pending interest for: ${interest.opportunite_titre}`);
          // Remove from pending list
          pendingInterests.splice(i, 1);
          i--;
        } catch (error) {
          console.error(`Failed to retry interest for ${interest.opportunite_titre}:`, error);
          // If it's not a network error, keep it for later retry
          if (error.code !== "ERR_NETWORK" && !error.message.includes("Network Error")) {
            // Remove if it's a permanent error (like 401, 403, etc.)
            pendingInterests.splice(i, 1);
            i--;
          }
        }
      }
      
      localStorage.setItem("pending_interests", JSON.stringify(pendingInterests));
      if (pendingInterests.length === 0) {
        toast.success("Toutes vos demandes en attente ont été envoyées avec succès !");
      }
    };
    
    // Retry every 30 seconds if there are pending interests
    const interval = setInterval(() => {
      const pendingInterests = JSON.parse(localStorage.getItem("pending_interests") || "[]");
      if (pendingInterests.length > 0) {
        retryPendingInterests();
      }
    }, 30000);
    
    // Initial retry
    retryPendingInterests();
    
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCA = (value) => {
    if (!value) return "-";
    if (typeof value === "string" && value.includes("€")) {
        return value;
    }
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return formatCurrency(num);
  };

  const getLocalisationLabel = (value) => {
    const loc = LOCALISATIONS.find(l => l.value === value);
    return loc ? loc.label : value;
  };

  const getSecteurLabel = (value) => {
    const sec = SECTEURS.find(s => s.value === value);
    return sec ? sec.label : value;
  };

  return (
    <div className="min-h-screen bg-background py-16 px-8" data-testid="opportunites-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-16">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-foreground/60 hover:text-primary transition-colors font-medium"
            data-testid="back-home"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
          <span className="text-3xl font-bold text-primary">Sequenti</span>
        </div>

        {/* Title */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Opportunités de reprise
          </h1>
          <p className="text-xl text-foreground/60 max-w-2xl mx-auto">
            {isQualified 
              ? "Découvrez les entreprises à céder correspondant à vos critères."
              : "Aperçu des opportunités. Complétez votre profil pour manifester votre intérêt."
            }
          </p>
        </div>

        {/* Filters */}
        <Card className="p-8 mb-12 border-none bg-white shadow-sm border border-border/40 rounded-lg">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" />
              <Input
                placeholder="Rechercher par mot-clé..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 text-lg rounded-lg border-border/50"
                data-testid="search-input"
              />
            </div>
            <Select value={secteurFilter} onValueChange={setSecteurFilter}>
              <SelectTrigger className="w-full md:w-56 h-14 text-lg rounded-lg border-border/50" data-testid="filter-secteur">
                <Filter className="w-5 h-5 mr-3 text-primary" />
                <SelectValue placeholder="Secteur" />
              </SelectTrigger>
              <SelectContent>
                {SECTEURS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={localisationFilter} onValueChange={setLocalisationFilter}>
              <SelectTrigger className="w-full md:w-48 h-14 text-lg rounded-lg border-border/50" data-testid="filter-localisation">
                <MapPin className="w-5 h-5 mr-3 text-primary" />
                <SelectValue placeholder="Région" />
              </SelectTrigger>
              <SelectContent>
                {LOCALISATIONS.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={budgetFilter} onValueChange={setBudgetFilter}>
              <SelectTrigger className="w-full md:w-48 h-14 text-lg rounded-lg border-border/50" data-testid="filter-budget">
                <Filter className="w-5 h-5 mr-3 text-primary" />
                <SelectValue placeholder="Budget" />
              </SelectTrigger>
              <SelectContent>
                {BUDGETS.map((b) => (
                  <SelectItem key={b.value} value={b.value}>
                    {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Results count */}
        <p className="text-sm font-bold uppercase tracking-widest text-foreground/40 mb-6 px-2">
          {filteredOpportunites.length} opportunité{filteredOpportunites.length > 1 ? "s" : ""} trouvée{filteredOpportunites.length > 1 ? "s" : ""}
        </p>

        {/* Opportunities Grid */}
        {loading ? (
          <div className="text-center py-24">
            <p className="text-xl text-foreground/40 animate-pulse">Chargement des opportunités...</p>
          </div>
        ) : filteredOpportunites.length === 0 ? (
          <Card className="p-24 text-center border-none bg-muted rounded-lg">
            <Building2 className="w-16 h-16 text-foreground/20 mx-auto mb-6" />
            <p className="text-xl font-bold text-primary/40">Aucune opportunité ne correspond à vos critères.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {filteredOpportunites.map((opp) => (
              <Card 
                key={opp.id} 
                className={`p-10 transition-all duration-300 rounded-lg bg-white border-none shadow-sm border border-border/40 hover:shadow-md cursor-pointer ${!isQualified ? "relative overflow-hidden" : ""}`}
                onClick={() => isQualified && setViewingDetails(opp)}
                data-testid={`opportunite-${opp.id}`}
              >
                {/* Blur overlay for non-qualified */}
                {!isQualified && (
                  <div className="absolute inset-0 bg-white/40 backdrop-blur-md z-10 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <div className="text-center p-8 bg-white/90 rounded-lg shadow-xl border border-border/40 max-w-xs mx-auto">
                      <Lock className="w-10 h-10 text-accent mx-auto mb-4" />
                      <p className="text-lg font-bold text-primary mb-6 leading-tight">Contenu réservé aux profils qualifiés</p>
                      <Button
                        size="lg"
                        className="bg-primary text-white hover:bg-primary/90 rounded-full w-full"
                        onClick={() => navigate("/repreneur")}
                        data-testid="qualify-button"
                      >
                        Compléter mon profil
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-primary mb-2 leading-tight">
                      {opp.titre}
                    </h3>
                    <div className="flex items-center gap-2 text-base text-foreground/60 font-medium">
                      <MapPin className="w-4 h-4 text-accent" />
                      {getLocalisationLabel(opp.localisation)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  <Badge className="bg-primary/10 text-primary border-none text-sm px-4 py-1 rounded-full font-bold">
                      {getSecteurLabel(opp.secteur)}
                  </Badge>
                  {interestedIds.has(opp.id) && (
                    <Badge className="bg-accent/10 text-accent border-accent/20 border text-sm px-4 py-1 rounded-full font-bold">
                      Intérêt manifesté
                    </Badge>
                  )}
                </div>

                <p className="text-lg text-foreground/70 mb-8 line-clamp-2 leading-relaxed italic">
                  "{opp.description}"
                </p>

                <div className="text-accent font-bold flex items-center gap-2">
                  En savoir plus <ArrowLeft className="w-4 h-4 rotate-180" />
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Opportunity Detail Dialog */}
        <Dialog open={!!viewingDetails} onOpenChange={() => setViewingDetails(null)}>
          <DialogContent className="sm:max-w-3xl p-0 overflow-hidden rounded-lg">
            {viewingDetails && (
              <div className="flex flex-col">
                <DialogHeader className="p-10 bg-primary text-white">
                  <DialogTitle className="sr-only">Détails de l'opportunité</DialogTitle>
                  <div className="flex flex-wrap gap-3 mb-4">
                    <Badge className="bg-accent text-white border-none px-4 py-1 rounded-full font-bold">
                      {getSecteurLabel(viewingDetails.secteur)}
                    </Badge>
                    {interestedIds.has(viewingDetails.id) && (
                      <Badge className="bg-white/20 text-white border-white/40 border px-4 py-1 rounded-full font-bold">
                        Intérêt manifesté
                      </Badge>
                    )}
                  </div>
                  <h2 className="text-4xl font-bold mb-4">
                    {viewingDetails.titre}
                  </h2>
                  <div className="flex items-center gap-4 text-white/80">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-accent" />
                      {getLocalisationLabel(viewingDetails.localisation)}
                    </div>
                  </div>
                </DialogHeader>

                <div className="p-10 space-y-10">
                  <div className="grid grid-cols-3 gap-8 bg-muted p-8 rounded-lg">
                    <div>
                      <p className="text-foreground/40 text-xs font-bold uppercase tracking-wider mb-2">CA Annuel</p>
                      <p className="text-2xl font-bold text-primary">{formatCA(viewingDetails.chiffre_affaires)}</p>
                    </div>
                    <div>
                      <p className="text-foreground/40 text-xs font-bold uppercase tracking-wider mb-2">Effectif</p>
                      <p className="text-2xl font-bold text-primary">{viewingDetails.effectif} salariés</p>
                    </div>
                    <div>
                      <p className="text-foreground/40 text-xs font-bold uppercase tracking-wider mb-2">Prix demandé</p>
                      <p className="text-2xl font-bold text-accent">{formatCurrency(viewingDetails.valorisation_max)}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-accent" />
                      Description de l'activité
                    </h3>
                    <p className="text-lg text-foreground/70 leading-relaxed italic">
                      "{viewingDetails.description}"
                    </p>
                  </div>

                  {viewingDetails.memorandum && (
                    <div className="bg-accent/5 p-6 rounded-lg border border-accent/20 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-accent uppercase tracking-wider mb-1">Documentation</p>
                        <p className="text-xl font-bold text-primary">Lien teaser</p>
                      </div>
                      <a 
                        href={viewingDetails.memorandum} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 bg-white text-accent border border-accent/20 hover:bg-accent hover:text-white px-6 py-3 rounded-lg transition-all font-bold shadow-sm"
                      >
                        Consulter <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>
                  )}


                  <Button
                    className={`w-full text-xl h-16 rounded-lg shadow-lg transition-all ${interestedIds.has(viewingDetails.id) ? "bg-muted text-foreground/40 cursor-not-allowed" : "bg-accent text-white hover:bg-accent/90 active:scale-95"}`}
                    disabled={interestedIds.has(viewingDetails.id)}
                    onClick={() => {
                      setSelectedOpportunite(viewingDetails);
                      setViewingDetails(null);
                    }}
                  >
                    <Send className="w-6 h-6 mr-3" />
                    {interestedIds.has(viewingDetails.id) ? "Intérêt déjà manifesté" : "Manifester mon intérêt"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Interest Dialog */}
        <Dialog open={!!selectedOpportunite} onOpenChange={() => setSelectedOpportunite(null)}>
          <DialogContent className="sm:max-w-xl p-10 rounded-lg">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-3xl font-bold text-primary mb-2">
                Manifester mon intérêt
              </DialogTitle>
              <DialogDescription className="text-lg font-semibold text-accent">
                {selectedOpportunite?.titre}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <p className="text-lg text-foreground/70 leading-relaxed">
                Votre demande sera transmise à <strong className="text-primary font-bold">Éric</strong> qui vous contactera personnellement pour organiser une présentation détaillée de l'entreprise.
              </p>


              <Textarea
                placeholder="Message optionnel (questions, motivations...)"
                value={interestMessage}
                onChange={(e) => setInterestMessage(e.target.value)}
                rows={4}
                className="text-lg p-6 rounded-lg border-border/50"
                data-testid="interest-message"
              />
            </div>
            <DialogFooter className="mt-8 gap-4">
              <Button variant="ghost" className="text-lg h-14 px-8" onClick={() => setSelectedOpportunite(null)}>
                Annuler
              </Button>
              <Button 
                onClick={handleShowInterest}
                disabled={sendingInterest}
                className="bg-accent text-white hover:bg-accent/90 text-lg h-14 px-10 rounded-lg shadow-md"
                data-testid="send-interest"
              >
                {sendingInterest ? "Envoi en cours..." : "Envoyer ma demande"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}